import { ref, computed, readonly } from 'vue'
import { defineStore } from 'pinia'
import type { JwtUserInfo } from '@/api/auth'
import { authApi, authUtils } from '@/api/auth'

export const useUserStore = defineStore('user', () => {
  // 状态
  const currentUser = ref<JwtUserInfo | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // 计算属性
  const isLoggedIn = computed(() => !!currentUser.value)
  const userName = computed(() => currentUser.value?.userName || '')
  const userId = computed(() => currentUser.value?.userId || 0)
  const userRoles = computed(() => currentUser.value?.roles || [])
  const userPermissions = computed(() => {
    const permissions = currentUser.value?.permissions || []
    return permissions
  })


  // 角色和权限检查方法
  const hasRole = (role: string) => {
    return userRoles.value.includes(role)
  }

  const hasAnyRole = (roles: string[]) => {
    return roles.some(role => userRoles.value.includes(role))
  }

  const hasPermission = (permission: string) => {
    return userPermissions.value.includes(permission)
  }

  const hasAnyPermission = (permissions: string[]) => {
    return permissions.some(permission => userPermissions.value.includes(permission))
  }

  const hasAllPermissions = (permissions: string[]) => {
    return permissions.every(permission => userPermissions.value.includes(permission))
  }

  // 设置用户信息
  const setUser = (user: JwtUserInfo) => {
    currentUser.value = user
    error.value = null
  }

  // 清除用户信息
  const clearUser = () => {
    currentUser.value = null
    error.value = null
  }

  // 获取用户信息（从服务器）
  const fetchUserInfo = async () => {
    if (isLoading.value) return

    isLoading.value = true
    error.value = null

    try {
      const response = await authApi.getProfile()
      
      if (response.success && response.data) {
        setUser(response.data)
        return response.data
      } else {
        error.value = response.message || '获取用户信息失败'
        return null
      }
    } catch (err: any) {
      error.value = err.message || '获取用户信息时发生错误'
      console.error('❌ 获取用户信息失败:', err)
      
      // 使用智能错误处理
      if (authUtils.handleApiError(err)) {
        clearUser()
      }
      
      return null
    } finally {
      isLoading.value = false
    }
  }

  // 刷新用户信息（强制从服务器获取最新数据）
  const refreshUserInfo = async () => {
    const userInfo = await fetchUserInfo()
    return userInfo
  }

  // 初始化用户状态（从本地存储恢复）
  const initUserState = () => {
    const storedUser = localStorage.getItem('currentUser')
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser)
        currentUser.value = userData
      } catch (err) {
        console.error('❌ 解析本地用户数据失败:', err)
        // 在开发环境中，跳过清除操作以保持localStorage数据
        if (import.meta.env.DEV) {
          console.log('开发环境：跳过清除用户数据，保持localStorage数据')
          return
        }
        localStorage.removeItem('currentUser')
      }
    }
  }

  return {
    // 状态
    currentUser: readonly(currentUser),
    isLoading: readonly(isLoading),
    error: readonly(error),
    
    // 计算属性
    isLoggedIn,
    userName,
    userId,
    userRoles,
    userPermissions,
    
    // 方法
    setUser,
    clearUser,
    fetchUserInfo,
    refreshUserInfo,
    initUserState,
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  }
}) 